import { compileDisplaySetSplitRules } from './compileSplitRules.js';
import { NO_VIEWPORT_TYPE } from './types.js';
const VOLUME_MODALITIES = ['CT', 'MR', 'PT', 'NM'];
const CINE_MODALITIES = ['US', 'XA', 'RF', 'ES', 'XC'];
const VOLUME_MULTIFRAME_SOP_CLASSES = [
    '1.2.840.10008.5.1.4.1.1.6.2',
    '1.2.840.10008.5.1.4.1.1.13.1.1',
    '1.2.840.10008.5.1.4.1.1.13.1.2',
];
const quotedList = (values) => `[${values.map((v) => `'${v}'`).join(', ')}]`;
const SINGLE_IMAGE_MODALITIES = ['CR', 'DX', 'MG'];
const RENDERABLE_IMAGE = { isImage: true, Rows: { notNull: true } };
export const displaySetRules = {
    video: {
        priority: 1,
        description: 'Instances encoded with a video transfer syntax, or a dedicated video SOP ' +
            'class, or a long multi-frame secondary capture. One display set per ' +
            'instance, shown on a video viewport.',
        viewportTypes: ['video'],
        matches: { isVideo: true },
        groupBy: ['SOPInstanceUID'],
    },
    ecg: {
        priority: 2,
        description: 'ECG / waveform SOP classes. One display set per instance, shown on a ' +
            'waveform viewport rather than an image viewport.',
        viewportTypes: ['ecg'],
        matches: { isEcg: true },
        groupBy: ['SOPInstanceUID'],
    },
    wholeslide: {
        priority: 3,
        description: 'VL Whole Slide Microscopy (or modality SM). All pyramid levels of the ' +
            'series form a single whole-slide display set.',
        viewportTypes: ['wholeslide'],
        matches: { isWsi: true },
        groupBy: ['SeriesInstanceUID'],
    },
    singleImageModality: {
        priority: 4,
        description: 'CR / DX / MG, which acquire one image per instance. Split within the ' +
            'series by a coarse image-size bucket so differently sized views (e.g. ' +
            'mammography projections) become separate stacks.',
        viewportTypes: ['stack'],
        context: {
            rowsBucket: 'round(Rows / 64)',
            columnsBucket: 'round(Columns / 64)',
            imageSizeBucket: { template: 'rows={rowsBucket}&cols={columnsBucket}' },
        },
        matches: { Modality: SINGLE_IMAGE_MODALITIES, ...RENDERABLE_IMAGE },
        groupBy: ['SeriesInstanceUID', 'imageSizeBucket'],
    },
    multiFrame: {
        priority: 5,
        description: 'Multi-frame cine clips: a multi-frame instance with a slice location, ' +
            'or a US, XA, RF, ES or XC multi-frame that is not a volume. One display ' +
            'set per InstanceNumber, flagged isClip with its frame count. Other ' +
            'multi-frame instances, such as an enhanced CT, go to the volume rules.',
        viewportTypes: ['stack'],
        context: {
            isCineClip: `SliceLocation != null || (Modality in ${quotedList(CINE_MODALITIES)}` +
                ` && !(SOPClassUID in ${quotedList(VOLUME_MULTIFRAME_SOP_CLASSES)}))`,
        },
        matches: {
            ...RENDERABLE_IMAGE,
            isMultiFrame: true,
            isCineClip: true,
        },
        groupBy: ['SeriesInstanceUID', 'InstanceNumber'],
        attributes: {
            isClip: true,
            numImageFrames: { expression: 'Number(NumberOfFrames)' },
        },
    },
    mixedDimensionalityBValue: {
        priority: 6,
        description: 'Diffusion MR that mixes 4D b-value frames with trailing frames that have ' +
            'none. The undefined-b-value frames are not part of the 4D set, so they ' +
            'split off - otherwise 4D rendering is applied to the 3D portion.',
        viewportTypes: ['volume', 'volume3d', 'stack'],
        context: {
            isMixedBValue: 'series.instances.count(DiffusionBValue) > 0 && ' +
                'series.instances.count(DiffusionBValue) < series.instances.count()',
            hasNoBValue: 'DiffusionBValue == null',
        },
        matches: { Modality: 'MR', isMixedBValue: true, ...RENDERABLE_IMAGE },
        groupBy: ['SeriesInstanceUID', 'hasNoBValue'],
    },
    volume3d: {
        priority: 7,
        description: 'Multi-slice CT / MR / PT / NM, which reconstruct into a volume. Defaults ' +
            'to MPR, with 3D and stack also allowed.',
        viewportTypes: ['volume', 'volume3d', 'stack'],
        matches: {
            Modality: VOLUME_MODALITIES,
            seriesInstanceCount: { greaterThan: 1 },
            ...RENDERABLE_IMAGE,
        },
        groupBy: ['SeriesInstanceUID'],
    },
    defaultImageRule: {
        priority: 8,
        description: 'Fallback for any remaining renderable image. Grouped one display set per ' +
            'series.',
        viewportTypes: ['stack', 'volume', 'volume3d'],
        matches: RENDERABLE_IMAGE,
    },
    unsupported: {
        priority: Number.MAX_SAFE_INTEGER,
        description: 'Catch-all for objects nothing can render (SEG, RTSTRUCT, RTDOSE, SR, ' +
            'PDF, presentation states, or an image whose Rows have not loaded). ' +
            'Produces one display set for each series, marked isDisplayable: false, ' +
            'so the objects are surfaced rather than silently dropped. Runs last.',
        viewportTypes: [NO_VIEWPORT_TYPE],
        groupBy: ['SeriesInstanceUID'],
    },
};
export function createDisplaySetSplitRules(rules = displaySetRules, options = {}) {
    return compileDisplaySetSplitRules(rules, options);
}
